import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ColyseusSDK, type Room } from "@colyseus/sdk";
import { getStoredAuthToken } from "../../Auth/serverSessionApi.ts";
import { useClientDispatch, useClientSelector } from "../../Redux/clientHooks.ts";
import { userIdSelector } from "../../Redux/clientSlice.ts";
import {
	clearSocial,
	clearSocialData,
	setSocialConnected,
	setSocialOverview,
	setSocialInvitations,
} from "../../Redux/social.slice.ts";
import type { AppInvitationView } from "../../Domain/Social/AppInvitation.ts";
import { SocialActivity, type SocialOverview } from "../../Domain/Social/SocialTypes.ts";
import { SocialNotifications } from "./SocialNotifications.tsx";

export function SocialConnection() {
	const userId = useClientSelector(userIdSelector),
		dispatch = useClientDispatch(),
		{ pathname } = useLocation();
	const activity = useRef(SocialActivity.Online),
		room = useRef<Room | null>(null);
	useEffect(() => {
		activity.current = pathname.startsWith("/matches/")
			? SocialActivity.PlayingCards
			: pathname.startsWith("/admin/worlds")
				? SocialActivity.Editing
				: pathname.startsWith("/worlds/") || pathname.startsWith("/world-sessions/")
					? SocialActivity.Exploring
					: SocialActivity.Online;
		room.current?.send("activity", activity.current);
	}, [pathname]);
	useEffect(() => {
		if (!userId) {
			dispatch(clearSocial());
			return;
		}
		let stopped = false,
			retry: ReturnType<typeof setTimeout> | undefined,
			delay = 1000;
		const sdk = new ColyseusSDK(window.location.origin);
		const reconnect = () => {
			if (stopped) return;
			dispatch(clearSocialData());
			clearTimeout(retry);
			retry = setTimeout(() => {
				void connect();
			}, delay);
			delay = Math.min(30_000, delay * 2);
		};
		const connect = async () => {
			try {
				const token = getStoredAuthToken();
				if (!token) {
					reconnect();
					return;
				}
				sdk.auth.token = token;
				const joined = await sdk.create("social", { token });
				if (stopped) {
					await joined.leave();
					return;
				}
				room.current = joined;
				delay = 1000;
				dispatch(setSocialConnected(true));
				joined.onMessage("overview", (overview: SocialOverview) => {
					if (!stopped) dispatch(setSocialOverview(overview));
				});
				joined.onMessage("invitations", (invitations: AppInvitationView[]) => {
					if (!stopped) dispatch(setSocialInvitations(invitations));
				});
				joined.onMessage("social-error", () => {
					dispatch(clearSocialData());
				});
				joined.onLeave(() => {
					room.current = null;
					reconnect();
				});
				joined.onError(() => {
					void joined.leave();
				});
				joined.send("activity", activity.current);
			} catch {
				reconnect();
			}
		};
		void connect();
		return () => {
			stopped = true;
			clearTimeout(retry);
			void room.current?.leave();
			room.current = null;
			dispatch(clearSocial());
		};
	}, [userId, dispatch]);
	return <SocialNotifications />;
}
